import React from "react";
import { BaseModal } from "../../atoms/base-modal";
import classnames from "classnames";
import { matchType } from "../../../application/domain/base/base-type";
import { ProcedureEntity } from "../../../application/domain/procedures/procedure-entity";
import { SelectedCondition } from "../../../application/service/smart-apply/applications/applications-service";
import { SmartApplyR } from "../../pages/smart-apply/route";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { Checkbox } from "@graffer-inc/console-design-kit";

type OperationState = "PREVIEW" | "CONFIRM" | "PROCESS";

export const BulkIssuePdfFileModal: React.FC<{
  isActive: boolean;
  isDownloadingSamplePDF: boolean;
  procedure: ProcedureEntity;
  selectedCondition: SelectedCondition;
  onExecute: (
    fileName: string,
    skipNotification: boolean,
  ) => Promise<string | undefined>;
  onPreview: () => void;
  onClose: () => void;
}> = ({
  isActive,
  isDownloadingSamplePDF,
  procedure,
  selectedCondition,
  onExecute,
  onPreview,
  onClose,
}) => {
  const [pageStatus, setPageState] = React.useState<OperationState>("PREVIEW");
  const [extensionExcludedFileName, setExtensionExcludedFileName] =
    React.useState(
      procedure.published?.template.pdfOutputSetting?.pdfOutputBaseFileName?.replace(
        ".pdf",
        "",
      ) ?? "",
    );
  const [skipNotification, setSkipNotification] = React.useState(false);
  const [taskId, setTaskId] = React.useState("");
  const isInvalidFileName = React.useMemo(
    () =>
      extensionExcludedFileName.length === 0 ||
      extensionExcludedFileName.length > 64,
    [extensionExcludedFileName],
  );

  return (
    <BaseModal
      title={matchType(pageStatus, {
        PREVIEW: "ファイルを交付",
        CONFIRM: "処理内容の確認",
        PROCESS: "ファイルを交付",
      })}
      isActive={isActive}
      onClose={onClose}
    >
      {matchType(pageStatus, {
        PREVIEW: (
          <>
            <section className="modal-card-body">
              <div className="my-4">
                <p>
                  ステータスが「処理中」の申請を対象に、帳票ファイルを一括で交付します。帳票ファイルは、手続き一覧の帳票レイアウトで作成したファイルです。
                </p>
              </div>
              <div className="field">
                <label htmlFor="name" className="label">
                  交付する帳票ファイルのサンプル
                </label>
                <p className="help has-text-weight-normal mb-4">
                  実際に交付する帳票ファイルのうち、最新の1件をダウンロードして確認できます。
                </p>
                <button
                  className="button"
                  onClick={onPreview}
                  disabled={isDownloadingSamplePDF}
                >
                  ダウンロード
                </button>
              </div>
              <div className="field">
                <label htmlFor="name" className="label">
                  交付する帳票ファイルの名前
                </label>
                <p className="help has-text-weight-normal mb-4">
                  交付する帳票ファイルの名前が編集できます。
                </p>
                <div className="field has-addons">
                  <input
                    className={classNames("control input", {
                      "is-danger": isInvalidFileName,
                    })}
                    value={extensionExcludedFileName}
                    onChange={(e) => {
                      setExtensionExcludedFileName(e.target.value);
                    }}
                  />
                  <span className="button is-static">.pdf</span>
                </div>
                {isInvalidFileName && (
                  <p className="help is-danger">
                    交付する帳票ファイルの名前は1文字以上、64文字以下の値を入力してください。
                  </p>
                )}
              </div>
              <div className="field">
                <Checkbox
                  label={"申請者へのメール通知"}
                  description={
                    "申請者に対して、帳票ファイルが交付されたことをメールで通知します。チェックを外すと、申請者に対してにファイルはアップロードされますが、通知は行われません。"
                  }
                  options={[
                    {
                      value: "sendNotificationByMail",
                      name: "申請者に対して交付が完了したことをメールで通知する",
                    },
                  ]}
                  values={!skipNotification ? ["sendNotificationByMail"] : []}
                  onChange={() => setSkipNotification(!skipNotification)}
                />
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                className={classnames("button is-info is-fullwidth")}
                onClick={() => {
                  setPageState("CONFIRM");
                }}
                disabled={isInvalidFileName}
              >
                内容の確認に進む
              </button>
            </footer>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <div className="field">
                <div className="field">
                  <label htmlFor="name" className="label">
                    交付対象の手続き
                  </label>
                  <div className="control">{procedure.name}</div>
                </div>
                <div className="field">
                  <label htmlFor="name" className="label">
                    交付する帳票ファイルの名前
                  </label>
                  <div className="control">{`${extensionExcludedFileName}.pdf`}</div>
                </div>
                <div className="field">
                  <label htmlFor="name" className="label">
                    交付対象の申請数
                  </label>
                  <div className="control">
                    {selectedCondition.csv
                      ? "CSVにより選択された申請"
                      : `${selectedCondition.applicationIds.length}件`}
                  </div>
                </div>
              </div>
              <div className="notification is-size-6">
                {skipNotification ? (
                  <p className="mb-1">
                    交付が完了しても、申請者に対してメールは送信されません。
                  </p>
                ) : (
                  <>
                    <p className="mb-1 has-text-weight-bold">
                      交付が完了すると、以下の処理が実行されます。
                    </p>
                    <p>
                      交付対象として選択した、ステータスが「処理中」の申請者に対するメールの送信
                    </p>
                  </>
                )}
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={() => {
                  setPageState("PREVIEW");
                }}
              >
                戻る
              </button>
              <button
                className={"button is-info is-fullwidth"}
                onClick={() => {
                  onExecute(
                    `${extensionExcludedFileName}.pdf`,
                    skipNotification,
                  ).then((id) => {
                    if (typeof id === "string") {
                      setTaskId(id);
                      setPageState("PROCESS");
                      window.open(
                        `${SmartApplyR.ApplicationTaskDetail.link({ id: id })}`,
                        "_blank",
                      );
                    }
                  });
                }}
              >
                ファイルを交付する
              </button>
            </footer>
          </>
        ),
        PROCESS: (
          <>
            <section className="modal-card-body">
              ファイルの交付を開始しました。処理状況は
              <Link
                to={SmartApplyR.ApplicationTaskDetail.link({
                  id: taskId,
                })}
                target="_blank"
              >
                こちら
                <i className="fas fa-external-link-alt"></i>
              </Link>
              から確認してください。
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={onClose}
                disabled={false}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
